import React, { useEffect, useRef, useState } from "react";
import { withSidebar } from "../hoc/withSidebar";
import { useTranslation } from "react-i18next";
import CommonContainerBox from "../custom/containers/CommonContainerBox";
import ResponsiveDataGrid from "../custom/containers/ResponsiveDataGrid";
import { useDispatch, useSelector } from "react-redux";
import ErrorMessage from "../custom/containers/ErrorMessage";
import actions from "../../actions";
import { getBcrPendingGridHeadings } from "./BcrPendingApprovalsConstant";
import { ResponsiveBox, CircularProgress, Grid, Typography } from "../primitives";

const BCR_REVIEW_STATUS_OPEN = "PENDING";

const BcrPendingApproval = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const {
    location: { state },
  } = props;

  const [loading, setLoading] = useState(true);
  const [pageNumber, setPageNumber] = useState(0);

  const pendingBcrReducer = useSelector((state) => state.pendingBcrReducer);
  const pendingBcrData = pendingBcrReducer?.pendingBcrData;
  const pendingBcrError = pendingBcrReducer?.pendingBcrError;

  const errorRef = useRef();

  const [searchValues] = useState({
    status: BCR_REVIEW_STATUS_OPEN,
    offset: 0,
    limit: 100,
  });

  useEffect(() => {
    if (errorRef.current) {
      errorRef.current.focus();
    }
  }, [pendingBcrError]);

  useEffect(() => {
    if (state?.fromDetailPage && pendingBcrReducer.searchValues) {
      Object.assign(searchValues, pendingBcrReducer.searchValues);
      setPageNumber(searchValues.offset / searchValues.limit);
      state.fromDetailPage = null;
    }
    loadResults();
  }, []);

  const loadResults = () => {
    setLoading(true);
    dispatch(actions.pendingBcrActions.getPendingBcrApprovals(searchValues, setLoading));
  };

  const paginate = (values) => {
    searchValues.offset = values.startOffset;
    if (searchValues.limit !== values.endOffset - values.startOffset) {
      searchValues.limit = values.endOffset - values.startOffset;
      setPageNumber(0);
    }
    loadResults();
  };

  const rows = (pendingBcrData || []).map((row) => ({ ...row, id: row.reviewId }));

  return (
    <div role="main">
      <ErrorMessage error={pendingBcrError} errorRef={errorRef} mt={2} />

      <CommonContainerBox
        marginTop="30px"
        padding="0px 24px 0px 24px"
        minHeight="calc(100vh - 280px)"
        title={t("erps.billermanagement.bcrpendingapprovals.page.title")}
      >
        <Grid container>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <Typography variant="h1">{t("erps.billermanagement.bcrpendingapprovals.page.title")}</Typography>
          </Grid>
          <ResponsiveBox mt={2}>
            <Grid item lg={11} md={11} sm={12} xs={12}>
              <Typography variant="body1">
                {t(
                  "erps.billermanagement.bcrpendingapprovals.page.description",
                  "Click on a transaction ID, in the table, to view details for the pending biller cross reference links and either approve or decline the proposed link."
                )}
              </Typography>
            </Grid>
            <ResponsiveBox mt={3} marginBottom={1}>
              <Typography variant="body1">
                {(pendingBcrData && !loading ? pendingBcrReducer.totalCount || 0 : 0) +
                  t("erps.billermanagement.bcrpendingapprovals.count.suffix", " pending approvals")}
              </Typography>
            </ResponsiveBox>
            {pendingBcrData && !loading ? (
              <ResponsiveBox>
                <ResponsiveDataGrid
                  data={{
                    rows,
                    count: pendingBcrReducer.totalCount || "0",
                    columns: getBcrPendingGridHeadings(),
                  }}
                  sumOfColumnWidth="true"
                  initialLoading={loading}
                  pageNumber={pageNumber}
                  setPage={setPageNumber}
                  rowPerPage={searchValues?.limit}
                  hideFooterPagination={false}
                  onAnyPaginationAction={paginate}
                />
              </ResponsiveBox>
            ) : (
              <ResponsiveBox ml={40} mt={10} sx={{ display: "flex" }}>
                <CircularProgress />
              </ResponsiveBox>
            )}
          </ResponsiveBox>
        </Grid>
      </CommonContainerBox>
    </div>
  );
};

export default withSidebar(BcrPendingApproval);
